import React from 'react';
import classnames from 'classnames';
import { get, isEmpty, map } from 'lodash';
import { DraggableVerticalList } from 'new/components/DraggableVerticalList';
import UIUtils from '../../../lib/UIUtils';
import Tooltip from '../../elements/mui/Tooltip';

/**
 * SVG icons
 */
import { ReactComponent as CheckIcon } from 'new/components/icons/fi-rr-check.svg';
import { ReactComponent as CloseIcon } from 'new/components/icons/fi-sr-cross.svg';
import { ReactComponent as MenuIcon } from 'new/components/icons/fi-sr-menu-burger.svg';
import { reorder } from 'new/utils';

const styles = {
  includeIcon: {
    enabled: {
      color: 'var(--color-greyscale-400)',
      viewBox: '0 0 30 30',
      cursor: 'pointer',
    },
    disabled: {
      color: 'var(--color-success-600)',
      viewBox: '0 0 30 30',
      cursor: 'unset',
    },
  },
  excludeIcon: {
    enabled: {
      color: 'var(--color-greyscale-400)',
      viewBox: '0 0 30 30',
      cursor: 'pointer',
    },
    disabled: {
      color: '#d52123',
      viewBox: '0 0 30 30',
      cursor: 'unset',
    },
  },
};

const SortableItem = ({ attr, excludeByAttrName, onToggleExclude }) => {
  const attrName = UIUtils.getAttrDisplayName(attr);
  const isExcluded = get(excludeByAttrName, attrName);

  const { __formatDisplay = '', __type = '' } = attr;
  const type = __formatDisplay || __type;
  const validType = typeof type === 'string' ? type : '';

  return (
    <div
      data-testid={`format-card-attribute-${attrName}`}
      className={classnames('format-card-attribute draggable', {
        'is-excluded': isExcluded,
      })}
    >
      <div className="format-card-attribute-left-section">
        <MenuIcon className="format-card-attribute-drag-handle" />
        <div className="format-card-attribute-name">{attrName}</div>
        <div className="format-card-attribute-type">{validType}</div>
      </div>
      <div className="format-card-attribute-right-section">
        {(isExcluded && (
          <Tooltip title="Include in output file" arrow={true}>
            <CheckIcon
              data-testid="include-attr-button"
              fontSize="large"
              style={styles.includeIcon.enabled}
              onClick={() => onToggleExclude(attrName)}
            />
          </Tooltip>
        )) || <CheckIcon fontSize="large" style={styles.includeIcon.disabled} />}
        {(!isExcluded && (
          <Tooltip title="Exclude from output file" arrow={true}>
            <CloseIcon
              data-testid="exclude-attr-button"
              fontSize="large"
              style={styles.excludeIcon.enabled}
              onClick={() => onToggleExclude(attrName)}
            />
          </Tooltip>
        )) || <CloseIcon fontSize="large" style={styles.excludeIcon.disabled} />}
      </div>
    </div>
  );
};

const onDragEvent = (dragEvent, dataToReorder = [], onSuccess) => {
  if (!dragEvent) {
    console.warn('Drag event is missing');
    return;
  }

  const { source, destination } = dragEvent;

  if (!source || !destination) {
    console.warn('Invalid drag operation: missing source or destination');
    return;
  }

  if (typeof source.index !== 'number' || typeof destination.index !== 'number') {
    console.warn('Invalid indices in drag operation');
    return;
  }

  if (!dataToReorder || !Array.isArray(dataToReorder)) {
    console.warn('No attributes array available to reorder');
    return;
  }

  onSuccess(reorder(dataToReorder, source.index, destination.index));
};

class FormatCardAttributes extends React.Component {
  onSortEnd = (dragEvent) => {
    onDragEvent(dragEvent, this.props.sharedAttributes, this.props.updateAttrs);
  };

  render() {
    const { sharedAttributes, excludeByAttrName, title, sortable, scrollHeight, onToggleExclude } =
      this.props;

    const attributesChips = map(sharedAttributes, (attr, index) => {
      const { __formatDisplay = '', __type = '' } = attr;
      const type = __formatDisplay || __type;
      const validType = typeof type === 'string' ? type : '';
      const displayName = UIUtils.getAttrDisplayName(attr);
      return (
        <div
          data-testid={`format-card-attribute-${displayName}`}
          key={index}
          className="format-card-attribute"
        >
          <div className="format-card-attribute-name">{displayName}</div>
          <div className="format-card-attribute-type">{validType}</div>
        </div>
      );
    });

    const hasAvailableAttributes = sharedAttributes || attributesChips.length !== 0;
    const availableAttributes = hasAvailableAttributes ? (
      sortable ? (
        <DraggableVerticalList
          items={sharedAttributes}
          onDragEnd={this.onSortEnd}
          excludeByAttrName={excludeByAttrName}
          onToggleExclude={onToggleExclude}
          renderDraggableList={(items) => {
            return items.map((item, index) => {
              const key = item.__id;
              return (
                <DraggableVerticalList.Draggable index={index} key={key} draggableId={key}>
                  {(provided) => (
                    <li
                      id={key}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <SortableItem
                        key={`item-${index}`}
                        index={index}
                        attr={item}
                        excludeByAttrName={excludeByAttrName}
                        onToggleExclude={onToggleExclude}
                      />
                    </li>
                  )}
                </DraggableVerticalList.Draggable>
              );
            });
          }}
        />
      ) : (
        <div
          className="format-card-attributes"
          style={{ height: scrollHeight, maxHeight: scrollHeight }}
        >
          {attributesChips}
        </div>
      )
    ) : null;

    return (
      <div aria-label="container" style={{ paddingBottom: 'var(--spacing-xl)' }}>
        <div className="available-title">
          <span className="title">
            {title || 'Available Attributes in Nexset'}
            {sortable ? ' (drag to reorder)' : ''}
          </span>
        </div>

        {(!sharedAttributes || isEmpty(sharedAttributes)) && (
          <div className="available-description">
            <p className="description">
              This Nexset does not have any attributes. Please add attributes from the available
              set.
            </p>
          </div>
        )}

        {availableAttributes}
      </div>
    );
  }
}

export default FormatCardAttributes;
